import { isAxiosError } from 'axios'
import { AI_CREDITS_ERROR_MESSAGES } from '@/lib/aiCreditsErrors'
import { AI_ROUTING_ERROR_MESSAGES } from '@/lib/aiRouting'
import { BILLING_ERROR_MESSAGES } from '@/lib/billingReminders'
import { DEAL_ERROR_MESSAGES } from '@/lib/deals'
import { LISTING_INTAKE_ERROR_MESSAGES } from '@/lib/listingIntakeErrors'
import { ONLINE_BILLING_ERROR_MESSAGES } from '@/lib/onlineBilling'
import { PRIVACY_ERROR_MESSAGES } from '@/lib/privacyErrors'
import { STUDIO_ERROR_MESSAGES } from '@/lib/studio'

export const GENERIC_ERROR = 'Algo deu errado. Tente novamente em instantes.'
export const CONNECTION_ERROR = 'Não foi possível conectar ao servidor. Verifique sua conexão e tente novamente.'

const INTERNAL_ERROR_MESSAGE = 'Internal server error'

export interface ApiFieldIssue {
  field: string
  code: string
  message: string
  params?: Record<string, unknown>
}

export interface ApiErrorBody {
  status: 'error'
  code: string
  message: string
  errors?: string[]
  fields?: ApiFieldIssue[]
  params?: Record<string, unknown>
  traceId: string
}

export interface ApiError {
  status: number | null
  code: string
  message: string
  errors: string[]
  fields: ApiFieldIssue[]
  params: Record<string, unknown>
  traceId?: string
  offline: boolean
}

const MESSAGE_BY_CODE: Record<string, string> = {
  AUTH_IDENTITY_UNAVAILABLE: 'Não foi possível validar seu acesso agora. Tente novamente em instantes.',
  AUTH_INVALID_CREDENTIALS: 'E-mail ou senha incorretos.',
  AUTH_OAUTH_EXCHANGE_FAILED: 'Não foi possível concluir o login com o Google.',
  AUTH_OAUTH_HANDOFF_INVALID: 'Este link de login expirou. Entre novamente.',
  AUTH_OAUTH_NOT_CONFIGURED: 'Login com Google não está disponível neste ambiente.',
  AUTH_OAUTH_PROVIDER_UNREACHABLE: 'O Google não respondeu. Tente novamente.',
  AUTH_PASSWORD_RESET_INVALID: 'Este link de recuperação expirou ou já foi usado. Peça um novo.',
  AUTH_SCOPE_FORBIDDEN: 'Você não tem permissão para esta ação.',
  AUTH_SIGNUP_CODE_EXPIRED: 'Este código expirou. Peça um novo.',
  AUTH_SIGNUP_CODE_INVALID: 'Código incorreto. Confira o e-mail e tente de novo.',
  AUTH_SIGNUP_EXPIRED: 'Seu cadastro expirou. Preencha os dados de novo.',
  AUTH_SUPER_ADMIN_REQUIRED: 'Esta área é restrita a administradores da plataforma.',
  AUTH_TOKEN_INVALID: 'Sua sessão expirou. Entre novamente.',
  AUTH_TOKEN_MISSING: 'Sua sessão expirou. Entre novamente.',
  ...AI_CREDITS_ERROR_MESSAGES,
  ...AI_ROUTING_ERROR_MESSAGES,
  ...BILLING_ERROR_MESSAGES,
  ...DEAL_ERROR_MESSAGES,
  ...LISTING_INTAKE_ERROR_MESSAGES,
  ...ONLINE_BILLING_ERROR_MESSAGES,
  ...PRIVACY_ERROR_MESSAGES,
  ...STUDIO_ERROR_MESSAGES,
  BILLING_PAYMENT_FAILED: 'O pagamento da diferença do plano foi recusado. Atualize a forma de pagamento em Gerenciar assinatura e tente de novo.',
  COMPANY_CNPJ_TAKEN: 'Este CNPJ já está cadastrado.',
  INVALID_CRON_EXPRESSION: 'A expressão cron ou o fuso horário são inválidos.',
  INVALID_CURSOR: 'A lista mudou enquanto você navegava. Recarregue para continuar.',
  INVITE_ALREADY_SENT: 'Já existe um convite pendente para este e-mail.',
  INVITE_NOT_FOUND: 'Convite não encontrado ou expirado.',
  MEMBERSHIP_ALREADY_EXISTS: 'Este usuário já faz parte da imobiliária.',
  MEMBERSHIP_LAST_OWNER_PROTECTED: 'A imobiliária precisa de pelo menos um proprietário.',
  MEMBERSHIP_SELF_EDIT_FORBIDDEN: 'Você não pode alterar o seu próprio acesso.',
  MEMBERSHIP_SELF_REMOVE_FORBIDDEN: 'Você não pode remover o seu próprio acesso.',
  METHOD_NOT_ALLOWED: 'Operação não suportada.',
  PLAN_CHANGE_NOT_ALLOWED: 'O plano Enterprise é alterado pela equipe da Habitar. Fale com a gente pelo WhatsApp.',
  PLAN_NOT_AVAILABLE: 'Este plano não está disponível para contratação pelo painel. Fale com a equipe da Habitar.',
  ROUTE_NOT_FOUND: 'Recurso não encontrado.',
  SCHEDULE_NAME_ALREADY_EXISTS: 'Já existe um agendamento com este nome.',
  SUBSCRIPTION_ALREADY_ACTIVE: 'A imobiliária já tem uma assinatura ativa. Troque de plano em vez de assinar de novo.',
  SUBSCRIPTION_INACTIVE: 'A assinatura da imobiliária está inativa.',
  SUBSCRIPTION_REQUIRED: 'A imobiliária não tem assinatura ativa para trocar de plano. Assine um plano primeiro.',
  TENANT_COMPANY_REQUIRED: 'Selecione uma imobiliária para continuar.',
  TRIAL_UPGRADE_REQUIRES_PAYMENT: 'Durante o teste grátis, o plano maior só é liberado depois do pagamento.',
  UPSTREAM_UNAVAILABLE: 'Serviço temporariamente indisponível. Tente novamente em instantes.',
  USER_EMAIL_TAKEN: 'Este e-mail já está cadastrado.',
  VALIDATION_FAILED: 'Confira os campos destacados.'
}

const isApiErrorBody = (data: unknown): data is ApiErrorBody =>
  typeof data === 'object' && data !== null && (data as ApiErrorBody).status === 'error' && typeof (data as ApiErrorBody).code === 'string'

function legacyMessage(data: unknown): string {
  const body = data as { message?: unknown; error?: unknown } | undefined
  if (typeof body?.message === 'string') return body.message
  if (Array.isArray(body?.message)) return body.message.join(' ')
  if (typeof body?.error === 'string') return body.error
  return ''
}

const isRuntimeError = (error: Error): boolean =>
  error instanceof TypeError || error instanceof ReferenceError || error instanceof SyntaxError || error instanceof RangeError

export function extractApiError(error: unknown): ApiError {
  if (isAxiosError(error)) {
    const status = error.response?.status ?? null
    const data = error.response?.data
    if (isApiErrorBody(data)) {
      return {
        status,
        code: data.code,
        message: data.message,
        errors: data.errors ?? [],
        fields: data.fields ?? [],
        params: data.params ?? {},
        traceId: data.traceId,
        offline: false
      }
    }
    return {
      status,
      code: error.response ? 'UNKNOWN_ERROR' : 'NETWORK_ERROR',
      message: error.response ? legacyMessage(data) : error.message,
      errors: [],
      fields: [],
      params: {},
      offline: !error.response
    }
  }
  return {
    status: null,
    code: 'UNKNOWN_ERROR',
    message: error instanceof Error && !isRuntimeError(error) ? error.message : '',
    errors: [],
    fields: [],
    params: {},
    offline: false
  }
}

export function errorMessage(error: unknown, fallback = GENERIC_ERROR): string {
  const details = extractApiError(error)
  if (details.offline) return CONNECTION_ERROR

  const mapped = MESSAGE_BY_CODE[details.code]
  if (mapped) return mapped

  const opaque = (details.status ?? 0) >= 500 || details.message === INTERNAL_ERROR_MESSAGE || !details.message
  if (!opaque) return details.message
  if (!details.traceId) return fallback
  return `${fallback} (código ${details.code}, referência ${details.traceId})`
}

export function fieldErrors(error: unknown): Record<string, string> {
  return extractApiError(error).fields.reduce<Record<string, string>>((acc, issue) => {
    if (!acc[issue.field]) acc[issue.field] = issue.message
    return acc
  }, {})
}

export const isErrorCode = (error: unknown, ...codes: string[]): boolean => codes.includes(extractApiError(error).code)

export function errorStatus(error: unknown): number | null {
  return isAxiosError(error) ? (error.response?.status ?? null) : null
}

export const isNotFound = (error: unknown): boolean => errorStatus(error) === 404
